import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { REGISTRATION_ENABLED } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import TennisWeekCarousel from "@/components/league/TennisWeekCarousel";
import { ArrowLeft, Calendar, MapPin, Trophy, Euro, User, Users, Search, Link, X, Clock, Share, AlertCircle, Target, Info, MessageCircle, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useUser } from "@/contexts/UserContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import AccessCodeEntry from './registration/AccessCodeEntry';
import PartnerSearchSection from './PartnerSearchSection';
import SplitPaymentStatusCard from './SplitPaymentStatusCard';
import { useSplitPaymentStatus } from '@/hooks/useSplitPaymentStatus';
import { ChevronDown, ChevronUp } from 'lucide-react';
import PrizesDialog from './PrizesDialog';
import FriendsAvatars from './FriendsAvatars';
import FormatInfoDialog from './FormatInfoDialog';
import { getAvailableModalities, getSubcategoriesForSport, getModalityDisplayForEvent, Sport, EventType } from '@/utils/sportRules';
import { getRegisteredFriends } from '@/utils/friendsData';
import { calculateGuaranteedMatches } from '@/utils/eventUtils';
import { useAllSubcategoryCounts } from '@/hooks/useAllSubcategoryCounts';
import { Lock } from 'lucide-react';
import { useTournamentRules } from '@/hooks/useTournamentRules';
import { usePrizes } from '@/hooks/usePrizes';
import { useParticipantCount } from '@/hooks/useParticipantCount';
import { createOrGetChat } from '@/hooks/useChats';
import { useLeagueSchedule, extractDateFromFormatted } from '@/hooks/useLeagueSchedule';
import { formatTo12h } from '@/utils/leagueCalendarUtils';
import { useUserRegistration } from '@/hooks/useUserRegistration';

interface Event {
  id: string; // UUID from database
  title: string;
  sport: string;
  date: string;
  endDate?: string;
  time: string;
  start_time?: string; // Start time from database (HH:MM:SS format)
  end_time?: string; // End time from database (HH:MM:SS format)
  location: string;
  registrationFee: number;
  categories: string[];
  imageUrl?: string;
  image_url?: string;
  genderCategories?: string[];
  subcategories?: Record<string, string[]>;
  organizer?: string;
  participants: number;
  maxParticipants: number;
  max_participants?: number; // Total capacity from all subcategories
  matchType?: 'singles' | 'dobles' | 'ambos'; // Match type from event data
  isLeague?: boolean;
  prizes?: any;
  registrationDeadline?: string; // Add registration deadline property
  tournamentFormat?: 'round-robin' | 'sumatoria-juegos' | 'eliminacion-directa'; // Tournament format
  leagueFormat?: 'round-robin' | 'sumatoria-juegos' | 'escalera-piramidal'; // League format
  group_rules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additional_rules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
    classification?: any;
  };
  game_days?: string[];
  ladderConfig?: {
    winnerType?: 'first-place' | 'elimination';
    eliminationPhase?: 'octavos' | 'cuartos' | 'semifinal' | 'final';
    eliminationConfig?: {
      singleDateSchedule?: {
        date?: string;
        startTime?: string;
        endTime?: string;
      };
      phaseSchedules?: Record<string, {
        date?: string;
        startTime?: string;
        endTime?: string;
      }>;
    };
  };
  address?: string;
  postal_code?: string;
  country?: string;
  state?: string;
  organizer_id?: string;
  rules?: string | Record<string, any>;
}

interface EventRegistrationFormProps {
  event: Event;
  onBack: () => void;
  onRegister: (data: any) => void;
  onContactOrganizer?: (organizerData: any) => void;
}

type DbCategoryData = {
  genderCategories: string[];
  subcategories: Record<string, string[]>;
};

const parseDbCategoryDataFromRules = (rules: unknown): DbCategoryData | null => {
  if (!rules) return null;

  let rulesData: any = rules;
  if (typeof rules === 'string') {
    try {
      rulesData = JSON.parse(rules);
    } catch {
      return null;
    }
  }

  const genderCategories: string[] = Array.isArray(rulesData?.selectedGenders) ? rulesData.selectedGenders : [];
  const subcategories: Record<string, string[]> = {};

  if (rulesData?.genderCategories && typeof rulesData.genderCategories === 'object') {
    Object.entries(rulesData.genderCategories).forEach(([gender, categories]) => {
      if (Array.isArray(categories)) {
        subcategories[gender] = categories.map((c: any) => c?.category ?? c).filter(Boolean);
      }
    });
  }

  // Fallback: tennis leagues store categories in ladderConfig.tennisCategories
  if (genderCategories.length === 0 && Object.keys(subcategories).length === 0) {
    const tc = rulesData?.ladderConfig?.tennisCategories;
    if (tc) {
      if (Array.isArray(tc.selectedGenders)) {
        genderCategories.push(...tc.selectedGenders);
      }
      if (tc.subcategoryDetails && typeof tc.subcategoryDetails === 'object') {
        Object.entries(tc.subcategoryDetails).forEach(([gender, details]) => {
          if (Array.isArray(details)) {
            subcategories[gender] = details.map((d: any) => d.name || d);
          }
        });
      }
    }
  }

  if (genderCategories.length === 0 && Object.keys(subcategories).length === 0) return null;

  return {
    genderCategories,
    subcategories,
  };
};

const EventRegistrationForm = ({ event, onBack, onRegister, onContactOrganizer }: EventRegistrationFormProps) => {
  const { user } = useUser();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t, language } = useLanguage();
  const { splitPayment, clear: clearSplitPayment, refetch: refetchSplitPayment } = useSplitPaymentStatus(event.id);
  const { registrations: userRegistrations } = useUserRegistration(event.id);
  const isAlreadyRegistered = userRegistrations.some(r => r.status === 'confirmed' && r.payment_status === 'completed');

  const handlePayPartner = (sp: import('@/hooks/useSplitPaymentStatus').SplitPaymentData) => {
    navigate(`/split-payment/${sp.id}`);
  };

  const handleChatWithOrganizer = async () => {
    if (!event.organizer_id) {
      toast.error(t('eventDetail.organizerNotFound'));
      return;
    }
    try {
      const chatId = await createOrGetChat(event.organizer_id);
      navigate(`/chat/${chatId}`);
    } catch (error) {
      toast.error(t('eventDetail.chatError'));
    }
  };
  
  // Get real prizes from database
  const { prizes: dbPrizes } = usePrizes(event.id);
  
  // Get tournament rules for guaranteed matches
  const { rules: tournamentRules } = useTournamentRules(event.id);
  
  // Component mount log for debugging
  useEffect(() => {
    console.log('EventRegistrationForm - Component mounted with event:', {
      id: event.id,
      title: event.title
    });
  }, []);
  
  // Get participant count from database
  const { participantCount, loading: countLoading } = useParticipantCount(event.id);
  
  // Fetch schedule data for leagues (organizer-managed dates from tournament_schedules table)
  const { gameDates, eliminationPhases, loading: scheduleLoading } = useLeagueSchedule(event.id);

  // Resolve ladderConfig: prefer prop, fallback to parsing from rules JSON
  const effectiveLadderConfig = useMemo(() => {
    if (event.ladderConfig) return event.ladderConfig;
    try {
      const rulesData = typeof event.rules === 'string'
        ? JSON.parse(event.rules)
        : event.rules;
      if (rulesData?.ladderConfig) {
        return {
          winnerType: rulesData.ladderConfig.winnerType,
          eliminationPhase: rulesData.ladderConfig.eliminationPhase,
          eliminationConfig: rulesData.ladderConfig.eliminationConfig,
        };
      }
      if (rulesData?.roundRobinConfig?.winnerType) {
        return {
          winnerType: rulesData.roundRobinConfig.winnerType,
          eliminationPhase: rulesData.roundRobinConfig.eliminationPhase,
          eliminationConfig: rulesData.roundRobinConfig.eliminationConfig,
        };
      }
    } catch {}
    return undefined;
  }, [event.ladderConfig, event.rules]);
  
  // DEBUG: Log para verificar los valores
  console.log('EventRegistrationForm - Debug:', {
    eventId: event.id,
    participantCount,
    countLoading,
    eventParticipantsField: event.participants
  });
  
  // Calculate display values for participants
  // For doubles: participantCount is in individuals, so divide by 2
  // max_participants from rules is already in pairs for doubles events
  const isDoubles = event.matchType === 'dobles';
  // Each registration record already represents a complete pair (not individuals)
  // So participantCount = number of pairs, no need to divide by 2
  const displayCount = participantCount;
  const displayMax = event.max_participants || event.maxParticipants || 0;
  const displayLabel = isDoubles ? t('eventDetail.pairs') : t('eventDetail.registered');
  
  // Check if the event is at full capacity
  const isEventFull = displayMax > 0 && displayCount >= displayMax;
  
  const [selectedModality, setSelectedModality] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGenderCategory, setSelectedGenderCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [hasSelectedPartner, setHasSelectedPartner] = useState(false);
  const [paymentMode, setPaymentMode] = useState<'full_amount' | 'my_part'>('full_amount');
  const [showFormatDialog, setShowFormatDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showAccessCodeEntry, setShowAccessCodeEntry] = useState(false);
  const [dbCategoryData, setDbCategoryData] = useState<DbCategoryData | null>(null);
  const [dbRawRules, setDbRawRules] = useState<string | null>(null);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [showPartnerSearch, setShowPartnerSearch] = useState(false);
  const [partnerSearchCategory, setPartnerSearchCategory] = useState('');
  const [partnerSearchSubcategory, setPartnerSearchSubcategory] = useState('');
  const [hasActivePartnerSearch, setHasActivePartnerSearch] = useState(false);

  // Auto-detect if user has an active partner search listing for this event
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;

    const checkActiveListing = async () => {
      const { data } = await supabase
        .from('partner_search_listings')
        .select('category, subcategory')
        .eq('event_id', event.id)
        .eq('user_id', authUser.id)
        .eq('status', 'searching')
        .maybeSingle();

      if (!cancelled && data) {
        setPartnerSearchCategory(data.category || '');
        setPartnerSearchSubcategory(data.subcategory || '');
        setShowPartnerSearch(true);
        setHasActivePartnerSearch(true);
      }
    };

    checkActiveListing();
    return () => { cancelled = true; };
  }, [authUser, event.id]);

  // Fetch category/subcategory config from DB (source of truth)
  // Priority: 1) rules.genderCategories, 2) gender_category + category/subcategory fields
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsCategoriesLoading(true);
      try {
        const { data, error } = await supabase
          .from('events')
          .select('rules, category, subcategory')
          .eq('id', event.id)
          .maybeSingle();

        if (error) throw error;

        // First try to parse from rules (structured format)
        let parsed = parseDbCategoryDataFromRules(data?.rules);
        
        // If no data from rules, try direct fields (category + subcategory only)
        if (!parsed && data && data.category) {
          const genderCategories: string[] = [data.category];
          const subcategories: Record<string, string[]> = {};
          
          if (data.subcategory) {
            subcategories[data.category] = [data.subcategory];
          }
          
          parsed = { genderCategories, subcategories };
        }
        
        if (!cancelled) {
          setDbCategoryData(parsed);
          setDbRawRules(data?.rules ?? null);
        }
      } catch (err) {
        // Don't block UI; we will fall back to props if DB fetch fails
        console.warn('[EventRegistrationForm] Failed to load categories:', err);
      } finally {
        if (!cancelled) {
          setIsCategoriesLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [event.id]);

  const effectiveGenderCategories = (dbCategoryData?.genderCategories?.length)
    ? dbCategoryData.genderCategories
    : event.genderCategories;

  const effectiveSubcategories = (dbCategoryData && Object.keys(dbCategoryData.subcategories).length > 0)
    ? dbCategoryData.subcategories
    : event.subcategories;

  // Partner search subcategory availability
  const partnerSearchSubsList = partnerSearchCategory && effectiveSubcategories?.[partnerSearchCategory]
    ? effectiveSubcategories[partnerSearchCategory]
    : [];
  const parsedEventRules = useMemo(() => {
    const rulesSource = dbRawRules ?? event.rules;
    if (!rulesSource) return null;
    if (typeof rulesSource === 'object') return rulesSource;
    try { return JSON.parse(rulesSource as string); } catch { return null; }
  }, [dbRawRules, event.rules]);
  const { counts: partnerSubCounts } = useAllSubcategoryCounts(
    event.id,
    partnerSearchCategory || null,
    partnerSearchSubsList,
    parsedEventRules
  );

  // Auto-fill user data when component mounts or user changes
  useEffect(() => {
    console.log('EventRegistrationForm: auth user data in useEffect:', authUser);
    if (authUser) {
      // Extract full name from auth user metadata or profile
      const fullName = authUser.user_metadata?.full_name || 
                      `${authUser.user_metadata?.first_name || ''} ${authUser.user_metadata?.last_name || ''}`.trim() ||
                      authUser.email?.split('@')[0] || '';
      
      console.log('EventRegistrationForm: Setting auto-fill data:', {
        name: fullName,
        email: authUser.email,
        phone: authUser.user_metadata?.phone || ''
      });
      
      setPlayerName(fullName);
      setEmail(authUser.email || '');
      setPhone(authUser.user_metadata?.phone || '');
    } else {
      console.log('EventRegistrationForm: No auth user data available');
    }
  }, [authUser]);

  // Get event's modality display text (for showing event info)
  const getEventModalityDisplay = () => {
    if (!event.matchType) return 'Dobles'; // Default fallback
    
    const sport = event.sport as Sport;
    const isLeague = event.isLeague || event.title.toLowerCase().includes('liga');
    const eventType: EventType = isLeague ? 'league' : 'tournament';
    const format = isLeague ? event.leagueFormat : event.tournamentFormat;
    
    return getModalityDisplayForEvent(sport, eventType, event.matchType, format);
  };

  // Get available modalities for user selection (for registration options)
  const getAvailableModalitiesForEvent = () => {
    // If event has a specific matchType, only show that modality
    if (event.matchType) {
      switch (event.matchType) {
        case 'singles':
          return ['Singles'];
        case 'dobles':
          return ['Dobles'];
        case 'ambos':
          return ['Singles', 'Dobles']; // For 'ambos', show both individual options
        default:
          return ['Dobles'];
      }
    }
    
    // Fallback: use sport rules to determine available modalities
    const sport = event.sport as Sport;
    const isLeague = event.isLeague || event.title.toLowerCase().includes('liga');
    const eventType: EventType = isLeague ? 'league' : 'tournament';
    const format = isLeague ? event.leagueFormat : event.tournamentFormat;
    
    // Use format directly
    
    const modalities = getAvailableModalities(sport, eventType, format);
    return modalities.map(modality => {
      switch (modality) {
        case 'singles':
          return 'Singles';
        case 'doubles':
          return 'Dobles';
        case 'ambos':
          return 'Singles y Dobles';
        default:
          return 'Dobles';
      }
    });
  };

  // Get price format - Updated to handle leagues vs tournaments differently
  const getPriceFormat = () => {
    const sport = event.sport.toLowerCase();
    const isLeague = event.isLeague || event.title.toLowerCase().includes('liga');
    const isTournament = event.title.toLowerCase().includes('torneo');

    if (isLeague) {
      switch (event.matchType) {
        case 'singles':
          return t('eventDetail.perPerson');
        case 'dobles':
          return t('eventDetail.perPair');
        case 'ambos':
          return t('eventDetail.perParticipation');
        default:
          return t('eventDetail.perPersonPair');
      }
    }

    if (isTournament) {
      if (sport === 'pádel') {
        return t('eventDetail.perPair');
      }
      if (sport === 'tenis' || sport === 'pickleball') {
        switch (event.matchType) {
          case 'singles':
            return t('eventDetail.perPerson');
          case 'dobles':
            return t('eventDetail.perPair');
          case 'ambos':
            return t('eventDetail.perParticipation');
          default:
            return t('eventDetail.perPair');
        }
      }
    }

    return t('eventDetail.perPerson');
  };

  const availableModalities = getAvailableModalitiesForEvent();
  const priceFormat = getPriceFormat();
  
  // Auto-select modality if there's only one available
  useEffect(() => {
    if (availableModalities.length === 1 && !selectedModality) {
      setSelectedModality(availableModalities[0]);
    }
  }, [availableModalities, selectedModality]);

  // Add function to get league format display
  const getLeagueFormatDisplay = (format?: string) => {
    switch (format) {
      case 'round-robin':
        return 'Round Robin';
      case 'sumatoria-juegos':
        return 'Sumatoria de Juegos';
      case 'escalera-piramidal':
        return 'Escalera';
      default:
        return null;
    }
  };

  // Add function to get league format badge color
  const getLeagueFormatBadgeColor = (format?: string) => {
    switch (format) {
      case 'round-robin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'sumatoria-juegos':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'escalera-piramidal':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Add function to get tournament format display
  const getTournamentFormatDisplay = (format?: string) => {
    switch (format) {
      case 'round-robin':
        return 'Round Robin';
      case 'sumatoria-juegos':
        return 'Sumatoria de Juegos';
      case 'eliminacion-directa':
        return 'Eliminación Directa';
      default:
        return null;
    }
  };

  // Add function to get tournament format badge color
  const getTournamentFormatBadgeColor = (format?: string) => {
    switch (format) {
      case 'round-robin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'sumatoria-juegos':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'eliminacion-directa':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Helper to parse date-only strings as local dates (avoids UTC offset issues)
  // Handles PostgreSQL timestamp format "YYYY-MM-DD HH:mm:ss+00"
  const parseLocalDate = (dateStr: string): Date => {
    if (!dateStr) return new Date();
    
    // Handle ISO format with 'T'
    if (dateStr.includes('T')) return new Date(dateStr);
    
    // Extract only the date portion before any space (handles "YYYY-MM-DD HH:mm:ss+00")
    const datePart = dateStr.split(' ')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    
    return new Date(year, month - 1, day);
  };

  // Add function to generate league calendar dates
  const generateLeagueCalendar = () => {
    if (!event.date || !event.endDate) return [];
    
    const startDate = parseLocalDate(event.date);
    const endDate = parseLocalDate(event.endDate);
    const dates = [];
    
    // Calculate total weeks including both start and end dates
    const diffInDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
    const totalWeeks = Math.floor(diffInDays / 7) + 1;
    
    // Generate weekly dates (assuming weekly matches)
    for (let i = 0; i < totalWeeks; i++) {
      const matchDate = new Date(startDate);
      matchDate.setDate(startDate.getDate() + (i * 7));
      dates.push(matchDate);
    }
    
    return dates;
  };

  // Add function to get challenge level for pyramid format
  const getChallengeLevel = (weekIndex: number) => {
    // This would come from event data in real implementation
    // For demo purposes, alternating between 1 and 2 position challenges
    return weekIndex % 2 === 0 ? 1 : 2;
  };

  // Add function to get circle color based on format and challenge level
  const getCircleColor = (weekIndex: number) => {
    if (event.leagueFormat === 'escalera-piramidal') {
      const challengeLevel = getChallengeLevel(weekIndex);
      return challengeLevel === 1 ? 'bg-orange-400' : 'bg-red-400';
    }
    return 'bg-gradient-to-br from-primary/60 to-primary';
  };

  // Add function to calculate total weeks
  const getTotalWeeks = () => {
    if (!event.date || !event.endDate) return 0;
    const startDate = parseLocalDate(event.date);
    const endDate = parseLocalDate(event.endDate);
    const diffInDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
    return Math.floor(diffInDays / 7) + 1;
  };

  // Reset form when modality changes
  const handleModalityChange = (modality: string) => {
    setSelectedModality(modality);
    setSelectedGenderCategory('');
    setSelectedSubcategory('');
    setPartnerName('');
    setSelectedPartner('');
    setHasSelectedPartner(false);
    setPaymentMode('full_amount'); // Reset payment mode when modality changes
  };

  // Get available subcategories from event data - Use event-specific subcategories
  const getAvailableSubcategories = () => {
    if (!selectedGenderCategory) return [];
    
    // If event has specific subcategories defined, use those
    if (event.subcategories && event.subcategories[selectedGenderCategory]) {
      return event.subcategories[selectedGenderCategory];
    }
    
    // Fallback to sport rules if no specific subcategories are defined
    const sport = event.sport as Sport;
    const categoryKey = selectedGenderCategory.toLowerCase() === 'mixtos' ? 'mixta' : 
                       selectedGenderCategory.toLowerCase() === 'masculina' ? 'masculina' : 'femenina';
    
    return getSubcategoriesForSport(sport, categoryKey);
  };

  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel':
      case 'padel':
        return <img src="/lovable-uploads/5b084fa7-1691-4ba9-84d3-9c2aaabd012a.png" alt="Pádel" className="w-8 h-8" />;
      case 'tenis':
        return '🎾';
      case 'pickleball':
        return <img src="/lovable-uploads/1bcd1940-66b0-4c8c-aa95-3f6e609dd00b.png" alt="Pickleball" className="w-8 h-8" />;
      default:
        return '🎾';
    }
  };

  const getSportName = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'padel':
        return 'Pádel';
      case 'tenis':
        return 'Tenis';
      case 'pickleball':
        return 'Pickleball';
      default:
        return sport;
    }
  };


  const formatEventDate = (startDate: string, endDate?: string) => {
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const start = parseLocalDate(startDate);
    const startDay = start.getDate();
    const startMonth = start.toLocaleDateString(locale, { month: 'short' });
    const year = start.getFullYear();
    
    if (endDate && endDate !== startDate) {
      const end = parseLocalDate(endDate);
      const endDay = end.getDate();
      const endMonth = end.toLocaleDateString(locale, { month: 'short' });
      
      if (startMonth !== endMonth) {
        return `${startDay} ${startMonth}. - ${endDay} ${endMonth}. - ${year}`;
      }
      return `${startDay} - ${endDay} ${startMonth}. - ${year}`;
    }
    
    return `${startDay} ${startMonth}. - ${year}`;
  };

  // Helper function to convert 24-hour time to 12-hour format with AM/PM
  const formatTimeTo12Hour = (time24: string): string => {
    const [hoursStr, minutesStr] = time24.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr?.slice(0, 2) || '00';
    
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12; // 0 hours = 12 AM, 12 hours = 12 PM
    
    return `${hours}:${minutes} ${period}`;
  };

  // Add function to format time range (2 hours duration)
  const formatTimeRange = (startTime: string) => {
    // If event has start_time and end_time from database, use those
    if (event.start_time != null && event.end_time != null && 
        typeof event.start_time === 'string' && event.start_time.length > 0 &&
        typeof event.end_time === 'string' && event.end_time.length > 0) {
      const startFormatted = formatTimeTo12Hour(event.start_time);
      const endFormatted = formatTimeTo12Hour(event.end_time);
      return `${startFormatted} - ${endFormatted}`;
    }
    
    // Validate startTime parameter before processing
    if (!startTime || typeof startTime !== 'string' || !startTime.includes(':')) {
      return '9:00 AM - 11:00 AM'; // Default fallback
    }
    
    // Parse the start time
    const [hours, minutes] = startTime.split(':').map(Number);
    
    // Validate parsed values
    if (isNaN(hours) || isNaN(minutes)) {
      return '9:00 AM - 11:00 AM'; // Default fallback
    }
    
    const startHour = hours;
    const startMinutes = minutes || 0;
    
    // Add 2 hours
    const endHour = (startHour + 2) % 24;
    const endMinutes = startMinutes;
    
    // Format both times with AM/PM
    const formatTime = (hour: number, min: number) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      let displayHour = hour % 12;
      if (displayHour === 0) displayHour = 12;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
    };
    
    return `${formatTime(startHour, startMinutes)} - ${formatTime(endHour, endMinutes)}`;
  };

  // Add function to format time based on event type (league vs tournament)
  const formatEventTime = (startTime: string, isLeague: boolean) => {
    if (isLeague) {
      // For leagues, show time range
      return formatTimeRange(startTime);
    } else {
      // For tournaments, show only start time
      return startTime;
    }
  };

  // Add function to calculate days until registration deadline
  const calculateDaysUntilDeadline = (deadline?: string) => {
    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatRegistrationDeadline = (deadline?: string) => {
    if (!deadline) return null;
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const deadlineDate = new Date(deadline);
    const day = deadlineDate.getDate();
    const month = deadlineDate.toLocaleDateString(locale, { month: 'short' });
    const year = deadlineDate.getFullYear();
    return `${day} ${month}. ${year}`;
  };

  // Get deadline display using same logic as EventCard
  const getDeadlineDisplay = () => {
    if (!event.registrationDeadline) return null;
    
    const daysLeft = calculateDaysUntilDeadline(event.registrationDeadline);
    const deadlineDate = formatRegistrationDeadline(event.registrationDeadline);
    
    if (daysLeft === null) return null;
    
    if (daysLeft < 0) {
      return {
        text: t('eventDetail.registrationsClosed'),
        color: 'text-red-600',
        bgColor: 'bg-red-100 text-red-800',
        date: deadlineDate
      };
    } else if (daysLeft <= 3) {
      return {
        text: `${daysLeft} ${daysLeft !== 1 ? t('eventDetail.daysLeft') : t('eventDetail.dayLeft')}`,
        color: 'text-red-600',
        bgColor: 'bg-red-100 text-red-800',
        date: deadlineDate
      };
    } else if (daysLeft <= 7) {
      return {
        text: `${daysLeft} ${t('eventDetail.daysLeft')}`,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 text-orange-800',
        date: deadlineDate
      };
    } else {
      return {
        text: `${daysLeft} ${t('eventDetail.daysLeft')}`,
        color: 'text-green-600',
        bgColor: 'bg-green-100 text-green-800',
        date: deadlineDate
      };
    }
  };

  const formatGenderCategoriesWithSubcategories = (genderCategories?: string[], subcategories?: Record<string, string[]>) => {
    if (!genderCategories || genderCategories.length === 0) {
      // Return null to indicate no categories - caller should show loading or placeholder
      return null;
    }
    
    // Normalize string for comparison (lowercase, remove accents, trim)
    const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    
    // Robust lookup function that finds subcategories even with naming variations
    const findSubcategories = (category: string): string[] => {
      // If no subcategories are defined in DB, return empty array - don't use generic fallback
      if (!subcategories || Object.keys(subcategories).length === 0) {
        return [];
      }
      
      // 1. Try exact match first
      if (subcategories[category]) {
        return subcategories[category];
      }
      
      // 2. Try normalized match
      const normalizedCategory = normalize(category);
      for (const key of Object.keys(subcategories)) {
        if (normalize(key) === normalizedCategory) {
          return subcategories[key];
        }
      }
      
      // 3. Try partial match (e.g., "Masculino" matching "Masculina")
      for (const key of Object.keys(subcategories)) {
        const normalizedKey = normalize(key);
        if (normalizedKey.includes(normalizedCategory) || normalizedCategory.includes(normalizedKey)) {
          return subcategories[key];
        }
      }
      
      // 4. No match found - return empty array (don't use generic fallback)
      console.log(`[formatGenderCategoriesWithSubcategories] No subcategories found for category "${category}", available keys:`, Object.keys(subcategories));
      return [];
    };
    
    // Always return structured data for vertical display when there are 2 or more categories
    if (genderCategories.length >= 2) {
      return genderCategories.map((category) => {
        const subs = findSubcategories(category);
        return {
          category,
          subcategories: subs
        };
      });
    }
    
    // For single category, return array with single item
    const subs = findSubcategories(genderCategories[0]);
    
    return [{
      category: genderCategories[0],
      subcategories: subs
    }];
  };

  const handleLocationClick = () => {
    console.log(`Navigate to club profile for: ${event.location}`);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Determine correct URL based on event type - check isLeague prop or leagueFormat
    const isLeagueEvent = event.isLeague || !!event.leagueFormat;
    const eventPath = isLeagueEvent ? 'league' : 'tournament';
    const eventUrl = `${window.location.origin}/${eventPath}/${event.id}`;
    
    const shareText = `¡Únete a ${event.title}! ${event.location} - ${formatEventDate(event.date, event.endDate)}`;
    
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: shareText,
        url: eventUrl,
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(eventUrl);
      toast.success(t('eventDetail.linkCopied'));
    }
  };

  const handleSearchPartner = () => {
    console.log("Searching for partner...");
    setSelectedPartner('María González');
    setHasSelectedPartner(true);
    console.log("Demo: Selected María González as partner");
  };

  const handleGenerateInviteLink = () => {
    console.log("Generating invite link...");
  };

  const handleRemovePartner = () => {
    setSelectedPartner('');
    setHasSelectedPartner(false);
    setPaymentMode('full_amount'); // Reset payment mode when removing partner
    console.log("Partner removed");
  };

  const calculateTotal = () => {
    if (selectedModality === 'Dobles' && paymentMode === 'my_part') {
      return event.registrationFee / 2;
    }
    return event.registrationFee;
  };

  // Function to get format description
  const getFormatDescription = (format: string, isLeague: boolean, sport?: string, gameType?: string) => {
    if (isLeague) {
      switch (format) {
        case 'round-robin':
          return 'Liga en dónde te enfrentarás cada semana con rivales diferentes. Gana y suma puntos para que al final de las semanas los mejores clasificados ganen el torneo.';
        case 'sumatoria-juegos':
          return 'Múltiples partidos cortos el mismo día. Ganador por mayor cantidad de juegos sumados.';
        case 'escalera-piramidal':
          // Check if it's Padel Singles for special description
          if (sport?.toLowerCase().includes('padel') && gameType === 'singles') {
            return 'Sistema específico de pádel con canchas y rotación de jugadores';
          }
          return 'Sistema de escalera dónde los participantes se retan para subir o bajar posiciones';
        default:
          return '';
      }
    } else {
      // Tournament formats
      switch (format) {
        case 'round-robin':
          return 'Torneo con fase de grupos en donde pasan los mejores del grupo a la fase eliminatoria.';
        case 'eliminacion-directa':
          return 'Torneo con draw por rondas donde el ganador avanza a la siguiente fase.';
        case 'sumatoria-juegos':
          return 'Torneo por tiempo en dónde los games que ganes en el set se van sumando. Al final los mejores pasan a la fase eliminatoria.';
        default:
          return '';
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const registrationData = {
      playerName,
      email,
      phone,
      selectedCategory,
      selectedGenderCategory,
      selectedSubcategory,
      selectedModality,
      partnerName: selectedModality === 'Dobles' ? (selectedPartner || partnerName) : '',
      eventId: event.id,
      totalAmount: calculateTotal()
    };
    onRegister(registrationData);
  };

  const handlePayment = () => {
    const registrationData = {
      eventId: event.id,
      mode: selectedModality === 'Dobles' ? 'parejas' : 'individual',
      partner: selectedPartner ? { name: selectedPartner } : null,
      paymentMode: paymentMode,
      amount: calculateTotal(),
      selectedCategory: selectedCategory,
      selectedModality: selectedModality
    };
    
    // Navigate to payment process with event data
    const eventData = encodeURIComponent(JSON.stringify(event));
    const paymentDataEncoded = encodeURIComponent(JSON.stringify(registrationData));
    navigate(`/payment?data=${paymentDataEncoded}&eventTitle=${encodeURIComponent(event.title)}&event=${eventData}`);
  };

  // Check if all required fields are filled for payment button
  const isReadyForPayment = () => {
    const basicInfoComplete = selectedModality && selectedGenderCategory && selectedSubcategory && playerName;
    
    if (selectedModality === 'Dobles') {
      return basicInfoComplete && (selectedPartner || partnerName);
    }
    
    return basicInfoComplete;
  };

  // Check if event is a league
  const isLeague = event.isLeague || event.title.toLowerCase().includes('liga');
  // Check if event is a tournament (not a league)
  const isTournament = event.title.toLowerCase().includes('torneo');

  const handlePrizesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // The dialog will handle opening
  };

  // Generate all category prizes similar to EventCard - now using real Supabase data
  const generateAllCategoryPrizes = () => {
    // First, try to use real prizes from database
    if (dbPrizes && dbPrizes.length > 0) {
      // Convert database prizes to CategoryPrizes format
      const categoryPrizesMap: Record<string, any> = {};
      
      dbPrizes.forEach(prize => {
        const key = prize.category;
        if (!categoryPrizesMap[key]) {
          // Parse category format: "Masculino 1era" or "Femenino 1era"
          const parts = prize.category.includes(' - ') ? prize.category.split(' - ') : [prize.category];
          let genderCategory = parts[0];
          let subcategory = parts[1] || '';
          
          // Handle formats like "Masculino 1era" (no dash separator)
          if (!subcategory && genderCategory.includes(' ')) {
            const spaceParts = genderCategory.split(' ');
            if (spaceParts.length >= 2) {
              genderCategory = spaceParts[0];
              subcategory = spaceParts.slice(1).join(' ');
            }
          }
          
          categoryPrizesMap[key] = {
            category: genderCategory,
            subcategory: subcategory || '1era',
            firstPlace: [],
            secondPlace: []
          };
        }
        
        // Parse prize description to determine type and place
        const description = prize.description.toLowerCase();
        const originalDescription = prize.description;
        const isSecondPlace = description.includes('segundo') || description.includes('2do') || description.includes('2°');
        
        let prizeObj;
        
        // Check for monetary values (like "$2000")
          if (originalDescription.includes('$') || originalDescription.includes('€') || description.includes('dinero') || prize.amount) {
          // Extract amount from description if it starts with $ or €
          let amount = prize.amount || 0;
          if (originalDescription.includes('$')) {
            const match = originalDescription.match(/\$(\d+)/);
            if (match) {
              amount = parseInt(match[1]);
            }
          } else if (originalDescription.includes('€')) {
            const match = originalDescription.match(/€(\d+)/);
            if (match) {
              amount = parseInt(match[1]);
            }
          }
          prizeObj = { type: 'dinero' as const, amount, description: originalDescription };
        } else if (description.includes('trofeo')) {
          prizeObj = { type: 'trofeo' as const, description: originalDescription };
        } else if (description.includes('certificado')) {
          prizeObj = { type: 'certificado' as const, description: originalDescription };
        } else if (description.includes('pala')) {
          prizeObj = { type: 'pala' as const, description: originalDescription };
        } else if (description.includes('raqueta')) {
          prizeObj = { type: 'raqueta' as const, description: originalDescription };
        } else {
          prizeObj = { type: 'otro' as const, description: originalDescription };
        }
        
        // Add prize to appropriate place
        if (isSecondPlace) {
          categoryPrizesMap[key].secondPlace?.push(prizeObj);
        } else {
          categoryPrizesMap[key].firstPlace.push(prizeObj);
        }
      });
      
      return Object.values(categoryPrizesMap);
    }

    // Fallback to event.prizes if available
    if (event.prizes && event.prizes.length > 0) {
      return event.prizes;
    }

    // Only show prizes from database — no hardcoded fallbacks
    return [];
  };

  const handleOrganizerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log(`Navigating to organizer profile: ${event.organizer}`);
    // Here you would typically navigate to the organizer's profile
    // For now, we'll just log it - the parent component should handle this
  };

  const calendarDates = generateLeagueCalendar();
  const totalWeeks = getTotalWeeks();
  const isTennisEvent = event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis';
  const shouldUseCarousel = totalWeeks > 8;

  return (
    <div className={`min-h-screen bg-gray-50`}>
        {/* SECTION 1: EVENT INFORMATION */}
        <Card className="bg-white shadow-lg">
          {/* Hero Image with Overlaid Controls */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-t-lg">
              <img 
                src={event.imageUrl || event.image_url || "/lovable-uploads/bb567a48-6ee6-4eea-917e-c2e42d492ea0.png"} 
                alt={`${event.title} - ${event.organizer}`}
                className="w-full h-44 md:h-52 object-cover cursor-pointer"
                onClick={() => setShowImageDialog(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30 pointer-events-none" />
              
              {/* Back Button - Top Left */}
              <button 
                onClick={onBack}
                className="absolute top-4 left-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
              >
                <ArrowLeft className="h-5 w-5 text-white" />
              </button>
              
              {/* Share Button - Top Right */}
              <button 
                onClick={handleShare}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
              >
                <Share className="h-5 w-5 text-white" />
              </button>
              
              {/* Glass Overlay with Event Info - Compact */}
              <div className={`absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md rounded-t-xl border-t border-x border-white/20 shadow-lg ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h1 className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-white drop-shadow-md truncate flex-1 mr-2`}>
                      {event.title}
                    </h1>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-white/90 capitalize`}>
                        {event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'padel' ? 'Pádel' : 
                         event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis' ? 'Tenis' : 'Pickleball'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-white/80 flex-shrink-0" />
                      {(event.address?.trim() || event.postal_code?.trim()) ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className={`${isMobile ? 'text-xs' : 'text-sm'} text-white/90 truncate underline decoration-dotted underline-offset-2 hover:text-white transition-colors text-left`}>
                              {event.location}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto max-w-[280px] p-3" side="bottom" align="start">
                            <div className="space-y-1">
                              <p className="font-semibold text-sm">{event.location}</p>
                              {event.address && <p className="text-sm text-muted-foreground">{event.address}</p>}
                              {event.postal_code && (
                                <p className="text-sm text-muted-foreground">CP: {event.postal_code}</p>
                              )}
                              {(event.state || event.country) && (
                                <p className="text-sm text-muted-foreground">
                                  {[event.state, event.country].filter(Boolean).join(', ')}
                                </p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-white/90 truncate`}>
                          {event.location}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Calendar className="h-3.5 w-3.5 text-white/80" />
                      <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-white/90`}>
                        {formatEventDate(event.date, event.endDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
           
           <CardContent className={`${isMobile ? 'p-5 pt-4' : 'p-6 pt-5'} relative`}>
            <div className="space-y-6">
              {/* Event Info */}
              <div className={`${isMobile ? 'space-y-5' : 'space-y-4'}`}>


                {/* Event Details - Lista unificada */}
                <div className={`${isMobile ? 'space-y-3' : 'space-y-3'}`}>

                  {/* Organizador */}
                  {event.organizer && (
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-primary" />
                      <span className="text-muted-foreground text-sm">
                        {t('eventDetail.organizer')}: <span className="font-medium text-foreground">{event.organizer}</span>
                      </span>
                    </div>
                  )}

                  {/* League Format - Clickeable (Solo para ligas) */}
                  {isLeague && event.leagueFormat && (
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-primary transition-colors group"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFormatDialog(true);
                      }}
                    >
                      <Info className="h-4 w-4 text-purple-500 group-hover:text-primary transition-colors" />
                      <span className="text-gray-600 text-sm group-hover:text-primary">
                        {t('eventDetail.format')}: <span className="font-medium text-gray-900 underline decoration-dotted group-hover:text-primary">
                          {(() => {
                            const normalizedFormat = event.leagueFormat?.replace(/_/g, '-') || 'round-robin';
                            return normalizedFormat === 'round-robin' ? 'Round Robin' : 
                                   normalizedFormat === 'sumatoria-juegos' ? 'Sumatoria de Juegos' : 'Escalera Piramidal';
                          })()}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Number of Matches - Only for leagues */}
                  {isLeague && totalWeeks > 0 && (
                    <div className="flex items-center gap-3">
                      <Target className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-600 text-sm">
                        {t('eventDetail.encounters')}: <span className="font-medium text-gray-900">{totalWeeks} {t('eventDetail.matches')}</span>
                      </span>
                    </div>
                  )}

                  {/* Registration Deadline */}
                  {(() => {
                    const deadlineDisplay = getDeadlineDisplay();
                    if (!deadlineDisplay) return null;
                    
                    return (
                      <div className="py-1">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-3 w-3 text-orange-500" />
                          <span className="text-gray-600 text-xs">
                            {t('eventDetail.registrationDeadline')}:
                          </span>
                          <Badge variant="outline" className={`text-xs ${deadlineDisplay.bgColor} ml-2`}>
                            {deadlineDisplay.text}
                          </Badge>
                        </div>
                      </div>
                    );
                  })()}


                   {/* Guaranteed Matches - Only for tournaments */}
                   {!event.isLeague && (
                      <div className="flex items-center gap-3">
                        <Target className="h-4 w-4 text-green-500" />
                        <span className="text-gray-600 text-sm">
                          {t('eventDetail.guaranteedMatches')}: <span className="font-bold text-green-600 text-lg">
                            {calculateGuaranteedMatches(
                              event.tournamentFormat,
                              false,
                              event.participants,
                              event.maxParticipants,
                              event.matchType,
                              tournamentRules?.playersPerGroup
                           )}
                          </span>
                        </span>
                      </div>
                   )}

                   {/* Categories and Modality Row */}
                   <div className="flex items-start gap-3">
                      <Trophy className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div className="w-full">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600 text-sm font-medium">{t('eventDetail.categories')}:</span>
                          {/* Modality on the right - Only show for leagues */}
                          {event.isLeague && (
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-gray-500" />
                              <span className="text-gray-600 text-sm font-medium">{t('eventDetail.modality')}:</span>
                              <span className="font-medium text-gray-900 text-sm">
                                {getEventModalityDisplay()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="font-medium text-foreground mt-1 text-sm">
                          {(() => {
                            // Show loading state while fetching categories
                            if (isCategoriesLoading) {
                              return (
                                <div className="flex items-center gap-2">
                                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                                  <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                                </div>
                              );
                            }
                            
                            const formatted = formatGenderCategoriesWithSubcategories(effectiveGenderCategories, effectiveSubcategories);
                            
                            // If no categories found, show placeholder
                            if (!formatted) {
                              return (
                                <span className="text-muted-foreground italic text-sm">
                                  {t('eventDetail.noCategories')}
                                </span>
                              );
                            }
                            
                            // Pickleball-only: show modality (Singles/Dobles) next to each category
                            const normalizedSport = (event.sport || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
                            const isPickleball = normalizedSport === 'pickleball';
                            let pickleballModalityLabel = '';
                            if (isPickleball && event.matchType) {
                              const mt = String(event.matchType).toLowerCase();
                              if (mt === 'singles') pickleballModalityLabel = t('subcategories.singles') !== 'subcategories.singles' ? t('subcategories.singles') : 'Singles';
                              else if (mt === 'dobles' || mt === 'doubles') pickleballModalityLabel = t('subcategories.doubles') !== 'subcategories.doubles' ? t('subcategories.doubles') : 'Dobles';
                              else if (mt === 'ambos') pickleballModalityLabel = t('subcategories.singlesAndDoubles') !== 'subcategories.singlesAndDoubles' ? t('subcategories.singlesAndDoubles') : 'Singles y Dobles';
                            }

                            // If it's an array (categories with subcategories), display vertically
                            if (Array.isArray(formatted)) {
                              return (
                                <div className="space-y-1">
                                  {formatted.map((item, index) => (
                                     <div key={index} className="text-sm">
                                       <span className="font-medium text-primary">
                                         {(() => {
                                           const key = item.category.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
                                           const translated = t(`categories.${key}`);
                                           return translated !== `categories.${key}` ? translated : item.category;
                                         })()}
                                         {item.subcategories.length > 0 && (
                                           <span className="text-muted-foreground ml-1">
                                             {item.subcategories.map(sub => {
                                               const translated = t(`subcategories.${sub}`);
                                               return translated !== `subcategories.${sub}` ? translated : sub;
                                             }).join(', ')}
                                           </span>
                                         )}
                                         {pickleballModalityLabel && (
                                           <span className="text-muted-foreground ml-1">
                                             {item.subcategories.length > 0 ? ' · ' : ''}{pickleballModalityLabel}
                                           </span>
                                         )}
                                       </span>
                                     </div>
                                  ))}
                                </div>
                              );
                            }
                            
                            // If it's a string, display as-is
                            return formatted;
                          })()}
                        </div>
                      </div>
                    </div>
                </div>

                {/* Registration Fee - Formatted like other list items */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="h-4 w-4 text-green-600 font-bold">$</span>
                    <span className="text-gray-600 text-sm">
                      {t('eventDetail.price')}: <span className="font-bold text-green-600 text-sm">${(event.matchType === 'dobles' || event.sport.toLowerCase() === 'pádel') ? Math.round(event.registrationFee / 2).toLocaleString(language === 'en' ? 'en-US' : 'es-MX') : event.registrationFee.toLocaleString(language === 'en' ? 'en-US' : 'es-MX')}</span>
                      <span className="text-gray-600 text-sm ml-1">{t('eventDetail.perPerson')}</span>
                    </span>
                  </div>
                  <PrizesDialog 
                    eventTitle={event.title}
                    sport={event.sport}
                    prizes={generateAllCategoryPrizes()}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className={`text-green-600 border-green-300 hover:bg-green-50 ${isMobile ? 'text-xs h-6 px-2' : 'text-sm h-8 px-4'}`}
                      onClick={handlePrizesClick}
                    >
                      <Trophy className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-1`} />
                      {t('eventDetail.viewPrizes')}
                    </Button>
                  </PrizesDialog>
                </div>
              </div>
            </div>

            {/* SECTION 2: CALENDAR - Only for Leagues - Now inside the same Card */}
            {isLeague && (
              <>
                {/* Separator between event info and calendar */}
                <div className="border-t border-gray-200 my-6"></div>
                
                <div className={`${isMobile ? 'space-y-4' : 'space-y-6'}`}>
                  <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>
                    {t('eventDetail.gameCalendar')}
                  </h3>
                  
                  {/* Optimized Schedule Info */}
                  <div className="flex flex-col items-center space-y-4">
                    <div className="flex items-center justify-center gap-8 bg-gray-50 rounded-full px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">
                          {(event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis')
                            ? t('eventDetail.bookEncounter')
                            : `${parseLocalDate(event.date).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { weekday: 'long' })}s de ${formatTimeRange(event.start_time || '')}`}
                        </span>
                      </div>
                      <div className="w-px h-6 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">
                          {totalWeeks} {t('eventDetail.weeks')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Weekly Calendar - Uses gameDates from tournament_schedules if available */}
                  <div className="flex justify-center">
                    {(() => {
                      // Build calendar weeks from DB gameDates (priority) or calculate dynamically (fallback)
                      const isTennisCalendar = event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis';
                      
                      const dateLocale = language === 'en' ? 'en-US' : 'es-ES';
                      const formatWeekDisplaySplit = (startDate: Date) => {
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + 6);
                        return {
                          start: startDate.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' }),
                          end: endDate.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' }),
                        };
                      };

                      const calendarWeeks = gameDates && gameDates.length > 0 
                        ? gameDates.map((gd, index) => {
                            const split = isTennisCalendar ? formatWeekDisplaySplit(new Date(gd.date)) : null;
                            return {
                              weekNumber: index + 1,
                              displayDate: split ? split.start : parseLocalDate(gd.date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'long' }).replace(' de ', ' '),
                              displayDateEnd: split ? split.end : undefined as string | undefined,
                            };
                          })
                        : Array.from({ length: totalWeeks }, (_, index) => {
                            const weekDate = parseLocalDate(event.date);
                            weekDate.setDate(weekDate.getDate() + (index * 7));
                            const split = isTennisCalendar ? formatWeekDisplaySplit(weekDate) : null;
                            return {
                              weekNumber: index + 1,
                              displayDate: split ? split.start : weekDate.toLocaleDateString(dateLocale, { day: 'numeric', month: 'long' }).replace(' de ', ' '),
                              displayDateEnd: split ? split.end : undefined as string | undefined,
                            };
                          });

                      if (isTennisCalendar) {
                        return <TennisWeekCarousel weeks={calendarWeeks} />;
                      }

                      return shouldUseCarousel ? (
                        <>
                          <Carousel className="w-full max-w-5xl">
                            <CarouselContent className="-ml-2 md:-ml-4">
                              {calendarWeeks.map((week) => (
                                <CarouselItem key={week.weekNumber} className={`pl-1 md:pl-4 ${isMobile ? 'basis-1/6' : 'basis-1/4 sm:basis-1/6 md:basis-1/8'}`}>
                                  <div className="flex flex-col items-center">
                                    <div className={`${isMobile ? 'w-12' : 'w-16 sm:w-20'} ${week.displayDateEnd ? (isMobile ? 'h-14' : 'h-20 sm:h-24') : (isMobile ? 'h-12' : 'h-16 sm:h-20')} rounded-lg bg-gradient-to-br from-primary to-primary/60 flex flex-col items-center justify-center text-primary-foreground font-bold shadow-lg hover:shadow-xl transition-shadow`}>
                                      <span className={`${isMobile ? 'text-xs' : 'text-xs'} font-semibold`}>S{week.weekNumber}</span>
                                      <span className={`${isMobile ? 'text-xs' : 'text-xs'} mt-1`}>{week.displayDate}</span>
                                      {week.displayDateEnd && (
                                        <span className={`${isMobile ? 'text-xs' : 'text-xs'}`}>{week.displayDateEnd}</span>
                                      )}
                                    </div>
                                  </div>
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                          </Carousel>
                          {isMobile && totalWeeks > 6 && (
                            <p className="text-[10px] text-muted-foreground text-center mt-2 animate-pulse">
                              {t('eventDetail.swipeMore')}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-wrap justify-center gap-4 max-w-6xl">
                          {calendarWeeks.map((week) => (
                            <div key={week.weekNumber} className="flex flex-col items-center">
                              <div className={`${isMobile ? 'w-16' : 'w-20'} ${week.displayDateEnd ? (isMobile ? 'h-20' : 'h-24') : (isMobile ? 'h-16' : 'h-20')} rounded-lg bg-gradient-to-br from-primary to-primary/60 flex flex-col items-center justify-center text-primary-foreground font-bold shadow-lg hover:shadow-xl transition-shadow`}>
                                <span className="text-xs font-semibold">S{week.weekNumber}</span>
                                <span className="text-xs mt-1">{week.displayDate}</span>
                                {week.displayDateEnd && (
                                  <span className="text-xs">{week.displayDateEnd}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>


                  {/* Playoff Calendar - For leagues with winnerType: elimination OR escalera-piramidal */}
                  {(() => {
                    // Show elimination section if:
                    // 1. ladderConfig.winnerType === 'elimination', OR
                    // 2. eliminationPhases has data from tournament_schedules
                    const hasEliminationPhases = eliminationPhases && Object.keys(eliminationPhases).length > 0;
                    const isEliminationWinner = effectiveLadderConfig?.winnerType === 'elimination';
                    const isEscalera = event.leagueFormat === 'escalera-piramidal';
                    const shouldShowElimination = (isEliminationWinner || hasEliminationPhases || isEscalera) && 
                      !(event.sport === 'pádel' && event.matchType === 'singles');
                    
                    if (!shouldShowElimination) return null;
                    
                    // Helper to get phase display label
                    const getPhaseLabel = (phaseName: string): string => {
                      const labels: Record<string, string> = {
                        'octavos': 'Octavos',
                        'cuartos': 'Cuartos',
                        'semifinal': 'Semis',
                        'final': 'Final'
                      };
                      return labels[phaseName.toLowerCase()] || phaseName;
                    };
                    
                    // Sort phases in correct order
                    const phaseOrder = ['octavos', 'cuartos', 'semifinal', 'final'];
                    
                    return (
                      <div className={`space-y-4 ${(event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis') ? 'mt-4' : 'mt-8'}`}>
                        <div className="flex items-center justify-center">
                          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-full"></div>
                          <span className="px-4 text-sm font-medium text-gray-600 bg-white whitespace-nowrap">{t('eventDetail.eliminationPhase')}</span>
                          <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent w-full"></div>
                        </div>
                        
                        {hasEliminationPhases ? (
                          // Use real dates from eliminationPhases (tournament_schedules)
                          <div className="flex justify-center">
                            <div className="flex flex-wrap justify-center gap-4 max-w-4xl">
                              {Object.entries(eliminationPhases)
                                .sort(([a], [b]) => phaseOrder.indexOf(a.toLowerCase()) - phaseOrder.indexOf(b.toLowerCase()))
                                .map(([phaseName, phaseData]) => {
                                  const phaseDate = new Date(phaseData.date);
                                  const phaseTime = phaseData.time || phaseData.startTime || '';
                                  
                                  return (
                                    <div key={phaseName} className="flex flex-col items-center">
                                      <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center text-white font-bold shadow-lg hover:shadow-xl transition-shadow">
                                        <span className="text-xs font-semibold">
                                          {getPhaseLabel(phaseName)}
                                        </span>
                                        <span className="text-xs mt-1">
                                          {phaseDate.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { month: 'short', day: 'numeric' })}
                                        </span>
                                        {phaseTime && (
                                          <span className="text-[10px] text-center whitespace-nowrap">
                                            {phaseTime}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : effectiveLadderConfig?.eliminationConfig?.phaseSchedules && Object.keys(effectiveLadderConfig.eliminationConfig.phaseSchedules).length > 0 ? (
                          // Case 2: Use real dates from ladderConfig.eliminationConfig.phaseSchedules (Tennis leagues)
                          (() => {
                            const phaseSchedules = effectiveLadderConfig!.eliminationConfig!.phaseSchedules!;
                            const isTennisElim = event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis';
                            
                            return (
                              <div className="flex justify-center">
                                <div className="flex flex-wrap justify-center gap-4 max-w-4xl">
                                  {Object.entries(phaseSchedules)
                                    .filter(([_, data]: [string, any]) => data?.date)
                                    .sort(([a], [b]) => phaseOrder.indexOf(a.toLowerCase()) - phaseOrder.indexOf(b.toLowerCase()))
                                    .map(([phaseName, phaseData]: [string, any]) => {
                                      const phaseDateMatch = (phaseData.date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
                                      const phaseDate = phaseDateMatch
                                        ? new Date(parseInt(phaseDateMatch[1]), parseInt(phaseDateMatch[2]) - 1, parseInt(phaseDateMatch[3]))
                                        : new Date(phaseData.date!);
                                      
                                      // For Tennis: show week range; for others: show single date
                                      let dateDisplay: { line1: string; line2?: string };
                                      dateDisplay = {
                                        line1: phaseDate.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { day: 'numeric', month: 'short' })
                                      };
                                      
                                      const timeStr = phaseData.startTime && phaseData.endTime
                                        ? `${formatTo12h(phaseData.startTime)} - ${formatTo12h(phaseData.endTime)}`
                                        : '';
                                      
                                      return (
                                        <div key={phaseName} className="flex flex-col items-center">
                                          <div className={`${isMobile ? 'w-16' : 'w-20'} ${dateDisplay.line2 ? (isMobile ? 'h-20' : 'h-24') : (isMobile ? 'h-16' : 'h-20')} rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center text-white font-bold shadow-lg hover:shadow-xl transition-shadow`}>
                                            <span className="text-xs font-semibold">
                                              {getPhaseLabel(phaseName)}
                                            </span>
                                            <span className="text-xs mt-1">
                                              {dateDisplay.line1}
                                            </span>
                                            {dateDisplay.line2 && (
                                              <span className="text-xs">
                                                {dateDisplay.line2}
                                              </span>
                                            )}
                                            {!isTennisElim && timeStr && (
                                              <span className="text-[10px] text-center whitespace-nowrap">
                                                {timeStr}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            );
                          })()
                        ) : effectiveLadderConfig?.eliminationConfig?.singleDateSchedule?.date ? (
                          // Case 2b: phaseSchedules empty but singleDateSchedule has data
                          (() => {
                            const elimConfig = effectiveLadderConfig!.eliminationConfig!;
                            const singleDate = elimConfig.singleDateSchedule!;
                            const startPhase = effectiveLadderConfig!.eliminationPhase || 'semifinal';
                            const isTennisElim = event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'tenis' || event.sport?.toLowerCase() === 'tennis';
                            
                            // Generate phases from startPhase to final
                            const allPhases = ['octavos', 'cuartos', 'semifinal', 'final'];
                            const startIdx = allPhases.indexOf(startPhase.toLowerCase());
                            const phases = startIdx >= 0 ? allPhases.slice(startIdx) : ['semifinal', 'final'];
                            
                            const dateMatch = (singleDate.date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
                            const baseDate = dateMatch
                              ? new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]))
                              : new Date(singleDate.date!);
                            
                            const timeStr = singleDate.startTime && singleDate.endTime
                              ? `${formatTo12h(singleDate.startTime)} - ${formatTo12h(singleDate.endTime)}`
                              : '';
                            
                            return (
                              <div className="flex justify-center">
                                <div className="flex flex-wrap justify-center gap-4 max-w-4xl">
                                  {phases.map((phaseName, idx) => {
                                    // When differentDates=false, all phases share the same base date
                                    let dateDisplay: { line1: string; line2?: string };
                                    dateDisplay = {
                                      line1: baseDate.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { day: 'numeric', month: 'short' })
                                    };
                                    
                                    return (
                                      <div key={phaseName} className="flex flex-col items-center">
                                        <div className={`${isMobile ? 'w-16' : 'w-20'} ${dateDisplay.line2 ? (isMobile ? 'h-20' : 'h-24') : (isMobile ? 'h-16' : 'h-20')} rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center text-white font-bold shadow-lg hover:shadow-xl transition-shadow`}>
                                          <span className="text-xs font-semibold">
                                            {getPhaseLabel(phaseName)}
                                          </span>
                                          <span className="text-xs mt-1">
                                            {dateDisplay.line1}
                                          </span>
                                          {dateDisplay.line2 && (
                                            <span className="text-xs">
                                              {dateDisplay.line2}
                                            </span>
                                          )}
                                          {!isTennisElim && timeStr && (
                                            <span className="text-[10px] text-center whitespace-nowrap">
                                              {timeStr}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          // Fallback: Calculate dates based on maxParticipants (original logic for escalera)
                          (() => {
                            const maxParticipants = event.maxParticipants || 16;
                            const getPlayoffPhases = () => {
                              if (maxParticipants >= 16) {
                                return [
                                  { name: 'Octavos', week: totalWeeks + 1 },
                                  { name: 'Cuartos', week: totalWeeks + 2 },
                                  { name: 'Semis', week: totalWeeks + 3 },
                                  { name: 'Final', week: totalWeeks + 4 }
                                ];
                              } else if (maxParticipants >= 8) {
                                return [
                                  { name: 'Cuartos', week: totalWeeks + 1 },
                                  { name: 'Semis', week: totalWeeks + 2 },
                                  { name: 'Final', week: totalWeeks + 3 }
                                ];
                              } else if (maxParticipants >= 4) {
                                return [
                                  { name: 'Semis', week: totalWeeks + 1 },
                                  { name: 'Final', week: totalWeeks + 2 }
                                ];
                              } else {
                                return [
                                  { name: 'Final', week: totalWeeks + 1 }
                                ];
                              }
                            };

                            const playoffPhases = getPlayoffPhases();
                            
                            return (
                              <div className="flex justify-center">
                                <div className="flex flex-wrap justify-center gap-4 max-w-4xl">
                                  {playoffPhases.map((phase) => {
                                    const phaseDate = new Date(event.date);
                                    phaseDate.setDate(phaseDate.getDate() + ((phase.week - 1) * 7));
                                    
                                    return (
                                      <div key={phase.name} className="flex flex-col items-center">
                                        <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex flex-col items-center justify-center text-white font-bold shadow-lg hover:shadow-xl transition-shadow">
                                          <span className="text-xs font-semibold">
                                            {phase.name}
                                          </span>
                                          <span className="text-xs mt-1">
                                            {phaseDate.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { month: 'short', day: 'numeric' })}
                                          </span>
                                          <span className="text-[10px] text-center whitespace-nowrap">
                                            {formatTimeRange(event.time)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()
                        )}
                        
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                            <Trophy className="h-5 w-5" />
                            {t('eventDetail.eliminationPhase')}
                          </h4>
                          <p className="text-sm text-purple-700">
                            {t('eventDetail.eliminationDesc')}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Registration Call-to-Action Buttons */}
                  <div className="pt-6 space-y-3">
                    {showAccessCodeEntry ? (
                      <AccessCodeEntry 
                        onBack={() => setShowAccessCodeEntry(false)}
                        onSuccess={() => {
                          window.location.reload();
                        }}
                        eventId={event.id}
                      />
                    ) : (
                      <>
                      {(() => {
                        const hasEventStarted = (() => {
                          const dateStr = event.date || (event as any).start_date;
                          if (!dateStr) return false;
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const startDate = parseLocalDate(dateStr);
                          startDate.setHours(0, 0, 0, 0);
                          return today >= startDate;
                        })();
                        const isDisabled = isEventFull || !REGISTRATION_ENABLED || hasEventStarted;
                        return (
                          <Button 
                            onClick={() => {
                              if (!isEventFull && REGISTRATION_ENABLED && !hasEventStarted) {
                                navigate(`/registration/${event.id}`, { state: { event } });
                              }
                            }}
                            disabled={isDisabled}
                            className={`w-full font-semibold text-lg px-8 py-3 rounded-lg shadow-lg transition-all duration-200 ${
                              isDisabled
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground hover:shadow-xl transform hover:scale-105'
                            }`}
                            size="lg"
                          >
                            {isEventFull ? (
                              <span className="flex items-center justify-center gap-2">
                                <AlertCircle className="h-5 w-5" />
                                {t('eventDetail.leagueFull')}
                              </span>
                            ) : hasEventStarted ? (
                              <span className="flex items-center justify-center gap-2">
                                <AlertCircle className="h-5 w-5" />
                                {t('eventDetail.registrationClosed') || 'Inscripciones cerradas'}
                              </span>
                            ) : (
                              t('eventDetail.signMeUp')
                            )}
                          </Button>
                        );
                      })()}

                          {/* Split Payment Status Card */}
                          {splitPayment && (
                            <div className="mt-4">
                              <SplitPaymentStatusCard
                                splitPayment={splitPayment}
                                onCancelled={clearSplitPayment}
                                onPayPartner={handlePayPartner}
                                onRefresh={refetchSplitPayment}
                              />
                            </div>
                          )}

                          {/* Busco Pareja - Block 1 (mobile) */}
                          {!isEventFull && (event.matchType === 'dobles' || (event.matchType as string) === 'doubles' || event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'padel') && (
                          <div className="space-y-3">
                            {hasActivePartnerSearch ? (
                              <PartnerSearchSection
                                eventId={event.id}
                                entryFee={event.registrationFee || 0}
                                category={partnerSearchCategory || null}
                                subcategory={partnerSearchSubcategory || null}
                                onSearchCancelled={() => {
                                  setHasActivePartnerSearch(false);
                                  setShowPartnerSearch(false);
                                }}
                              />
                            ) : (
                              <>
                                <Button
                                  variant="outline"
                                  onClick={() => !isAlreadyRegistered && setShowPartnerSearch(!showPartnerSearch)}
                                  disabled={isAlreadyRegistered}
                                  className="w-full border-primary text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Users className="h-4 w-4 mr-2" />
                                  {language === 'es' ? 'Busco Pareja' : 'Find a Partner'}
                                  {showPartnerSearch ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
                                </Button>

                                {isAlreadyRegistered && (
                                  <p className="text-xs text-center text-muted-foreground">
                                    {language === 'es' ? 'Ya estás inscrito en este torneo. ' : 'You are already registered for this tournament. '}
                                    <button className="text-primary underline font-medium" onClick={() => navigate('/', { state: { activeTab: 'my-events' } })}>
                                      {language === 'es' ? 'Ir a Mis Eventos' : 'Go to My Events'}
                                    </button>
                                  </p>
                                )}

                                {showPartnerSearch && !isAlreadyRegistered && (
                                  <div className="space-y-3 animate-in slide-in-from-top-2 duration-200 border rounded-lg p-4 bg-muted/30">
                                    <div className="relative flex items-center justify-center">
                                      <div className="absolute left-0 flex items-center gap-1 cursor-pointer" onClick={() => setShowPartnerSearch(false)}>
                                        <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                          {language === 'es' ? 'Cerrar' : 'Close'}
                                        </span>
                                      </div>
                                      <h4 className="text-sm font-semibold">
                                        {language === 'es' ? 'Busco Pareja' : 'Find a Partner'}
                                      </h4>
                                    </div>

                                    <p className="text-sm text-muted-foreground">
                                      {language === 'es'
                                        ? '¿No tienes pareja? Únete y te emparejaremos con otro jugador.'
                                        : "Don't have a partner? Join and we'll match you with another player."}
                                    </p>

                                    {effectiveGenderCategories && effectiveGenderCategories.length > 0 && (
                                      <div className="space-y-1">
                                        <Label className="text-xs font-semibold text-foreground">
                                          {language === 'es' ? 'Selecciona categoría' : 'Select category'}
                                        </Label>
                                        <div className="flex flex-wrap gap-2">
                                          {effectiveGenderCategories.map((cat: string) => (
                                            <Button
                                              key={cat}
                                              variant={partnerSearchCategory === cat ? 'default' : 'outline'}
                                              size="xs"
                                              onClick={() => { setPartnerSearchCategory(cat); setPartnerSearchSubcategory(''); }}
                                            >
                                              {cat}
                                            </Button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {partnerSearchCategory && effectiveSubcategories?.[partnerSearchCategory] && effectiveSubcategories[partnerSearchCategory].length > 0 && (
                                      <div className="space-y-1">
                                        <Label className="text-xs">{language === 'es' ? 'Subcategoría' : 'Subcategory'}</Label>
                                        <div className="flex flex-wrap gap-2">
                                          {effectiveSubcategories[partnerSearchCategory].map((sub: string) => {
                                            const info = partnerSubCounts[sub];
                                            const isFull = info?.isFull;
                                            const hasMax = info?.max !== null && info?.max !== undefined;
                                            return (
                                              <Button
                                                key={sub}
                                                variant={partnerSearchSubcategory === sub ? 'default' : 'outline'}
                                                size="xs"
                                                disabled={isFull}
                                                onClick={() => !isFull && setPartnerSearchSubcategory(sub)}
                                                className={`relative ${isFull ? 'opacity-50 grayscale' : ''}`}
                                              >
                                                {sub}
                                                {isFull ? (
                                                  <span className="flex items-center gap-0.5 ml-1">
                                                    <Lock className="h-3 w-3 text-destructive" />
                                                    <span className="text-[9px] text-destructive font-bold">
                                                      {language === 'es' ? 'Lleno' : 'Full'}
                                                    </span>
                                                  </span>
                                                ) : (
                                                   <Badge className={`ml-1 text-[9px] px-1 py-0 border-0 ${partnerSearchSubcategory === sub ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'}`}>
                                                    {info?.remaining != null ? info.remaining : ''}
                                                  </Badge>
                                                )}
                                              </Button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                     {((partnerSearchCategory && (partnerSearchSubcategory || !effectiveSubcategories?.[partnerSearchCategory]?.length)) || (!effectiveGenderCategories?.length)) && (
                                      <PartnerSearchSection
                                        eventId={event.id}
                                        entryFee={event.registrationFee || 0}
                                        category={partnerSearchCategory || null}
                                        subcategory={partnerSearchSubcategory || null}
                                        onSearchCancelled={() => {
                                          setHasActivePartnerSearch(false);
                                          setShowPartnerSearch(false);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Access Code + Chat Row */}
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost"
                            onClick={() => setShowAccessCodeEntry(true)}
                            className="flex-1 text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                          >
                            <KeyRound className="h-4 w-4 mr-2" />
                            {t('eventDetail.accessCode')}
                          </Button>
                          
                          {/* Chat Button - Contact Organizer */}
                          <Button 
                            variant="ghost"
                            onClick={handleChatWithOrganizer}
                            className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-3"
                            title={t('eventDetail.contactOrganizer')}
                          >
                            <MessageCircle className="h-5 w-5" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Registration Buttons - Only for TOURNAMENTS (not leagues) */}
            {!isLeague && (
              <div className="pt-6 space-y-3">
                {showAccessCodeEntry ? (
                  <AccessCodeEntry 
                    onBack={() => setShowAccessCodeEntry(false)}
                    onSuccess={() => window.location.reload()}
                    eventId={event.id}
                  />
                ) : (
                  <>
                    <Button 
                      onClick={() => {
                        if (!isEventFull && REGISTRATION_ENABLED) {
                          navigate(`/registration/${event.id}`, { state: { event } });
                        }
                      }}
                      disabled={isEventFull || !REGISTRATION_ENABLED}
                      className={`w-full font-semibold text-lg px-8 py-3 rounded-lg shadow-lg transition-all duration-200 ${
                        !REGISTRATION_ENABLED
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : isEventFull 
                          ? 'bg-gray-400 text-white cursor-not-allowed' 
                          : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white hover:shadow-xl transform hover:scale-105'
                      }`}
                      size="lg"
                    >
                      {isEventFull ? (
                        <span className="flex items-center justify-center gap-2">
                          <AlertCircle className="h-5 w-5" />
                          {t('eventDetail.tournamentFull')}
                        </span>
                      ) : (
                        t('eventDetail.signMeUp')
                      )}
                    </Button>

                    {/* Split Payment Status Card (desktop) */}
                    {splitPayment && (
                      <div className="mt-4">
                        <SplitPaymentStatusCard
                          splitPayment={splitPayment}
                          onCancelled={clearSplitPayment}
                          onPayPartner={handlePayPartner}
                          onRefresh={refetchSplitPayment}
                        />
                      </div>
                    )}

                    {/* Busco Pareja - Block 2 (desktop) */}
                    {!isEventFull && (event.matchType === 'dobles' || (event.matchType as string) === 'doubles' || event.sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === 'padel') && (
                      <div className="space-y-3">
                        {hasActivePartnerSearch ? (
                          <PartnerSearchSection
                            eventId={event.id}
                            entryFee={event.registrationFee || 0}
                            category={partnerSearchCategory || null}
                            subcategory={partnerSearchSubcategory || null}
                            onSearchCancelled={() => {
                              setHasActivePartnerSearch(false);
                              setShowPartnerSearch(false);
                            }}
                          />
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => !isAlreadyRegistered && setShowPartnerSearch(!showPartnerSearch)}
                              disabled={isAlreadyRegistered}
                              className="w-full border-primary text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Users className="h-4 w-4 mr-2" />
                              {language === 'es' ? 'Busco Pareja' : 'Find a Partner'}
                              {showPartnerSearch ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
                            </Button>

                            {isAlreadyRegistered && (
                              <p className="text-xs text-center text-muted-foreground">
                                {language === 'es' ? 'Ya estás inscrito en este torneo. ' : 'You are already registered for this tournament. '}
                                <button className="text-primary underline font-medium" onClick={() => navigate('/', { state: { activeTab: 'my-events' } })}>
                                  {language === 'es' ? 'Ir a Mis Eventos' : 'Go to My Events'}
                                </button>
                              </p>
                            )}

                            {showPartnerSearch && !isAlreadyRegistered && (
                              <div className="space-y-3 animate-in slide-in-from-top-2 duration-200 border rounded-lg p-4 bg-muted/30">
                                <div className="relative flex items-center justify-center">
                                  <div className="absolute left-0 flex items-center gap-1 cursor-pointer" onClick={() => setShowPartnerSearch(false)}>
                                    <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">
                                      {language === 'es' ? 'Cerrar' : 'Close'}
                                    </span>
                                  </div>
                                  <h4 className="text-sm font-semibold">
                                    {language === 'es' ? 'Busco Pareja' : 'Find a Partner'}
                                  </h4>
                                </div>

                                <p className="text-sm text-muted-foreground">
                                  {language === 'es'
                                    ? '¿No tienes pareja? Únete y te emparejaremos con otro jugador.'
                                    : "Don't have a partner? Join and we'll match you with another player."}
                                </p>

                                {effectiveGenderCategories && effectiveGenderCategories.length > 0 && (
                                  <div className="space-y-1">
                                    <Label className="text-xs font-semibold text-foreground">
                                      {language === 'es' ? 'Selecciona categoría' : 'Select category'}
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                      {effectiveGenderCategories.map((cat: string) => (
                                        <Button
                                          key={cat}
                                          variant={partnerSearchCategory === cat ? 'default' : 'outline'}
                                          size="xs"
                                          onClick={() => { setPartnerSearchCategory(cat); setPartnerSearchSubcategory(''); }}
                                        >
                                          {cat}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {partnerSearchCategory && effectiveSubcategories?.[partnerSearchCategory] && effectiveSubcategories[partnerSearchCategory].length > 0 && (
                                  <div className="space-y-1">
                                    <Label className="text-xs">{language === 'es' ? 'Subcategoría' : 'Subcategory'}</Label>
                                    <div className="flex flex-wrap gap-2">
                                      {effectiveSubcategories[partnerSearchCategory].map((sub: string) => {
                                        const info = partnerSubCounts[sub];
                                        const isFull = info?.isFull;
                                        const hasMax = info?.max !== null && info?.max !== undefined;
                                        return (
                                          <Button
                                            key={sub}
                                            variant={partnerSearchSubcategory === sub ? 'default' : 'outline'}
                                            size="xs"
                                            disabled={isFull}
                                            onClick={() => !isFull && setPartnerSearchSubcategory(sub)}
                                            className={`relative ${isFull ? 'opacity-50 grayscale' : ''}`}
                                          >
                                            {sub}
                                            {isFull ? (
                                              <span className="flex items-center gap-0.5 ml-1">
                                                <Lock className="h-3 w-3 text-destructive" />
                                                <span className="text-[9px] text-destructive font-bold">
                                                  {language === 'es' ? 'Lleno' : 'Full'}
                                                </span>
                                              </span>
                                            ) : (
                                              <Badge className={`ml-1 text-[9px] px-1 py-0 border-0 ${partnerSearchSubcategory === sub ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'}`}>
                                                {info?.remaining != null ? info.remaining : ''}
                                              </Badge>
                                            )}
                                          </Button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {((partnerSearchCategory && (partnerSearchSubcategory || !effectiveSubcategories?.[partnerSearchCategory]?.length)) || (!effectiveGenderCategories?.length)) && (
                                  <PartnerSearchSection
                                    eventId={event.id}
                                    entryFee={event.registrationFee || 0}
                                    category={partnerSearchCategory || null}
                                    subcategory={partnerSearchSubcategory || null}
                                    onSearchCancelled={() => {
                                      setHasActivePartnerSearch(false);
                                      setShowPartnerSearch(false);
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost"
                        onClick={() => setShowAccessCodeEntry(true)}
                        className="flex-1 text-slate-600 hover:text-slate-800"
                      >
                        <KeyRound className="h-4 w-4 mr-2" />
                        {t('eventDetail.accessCode')}
                      </Button>
                      
                      <Button 
                        variant="ghost"
                        onClick={handleChatWithOrganizer}
                        className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-3"
                        title={t('eventDetail.contactOrganizer')}
                      >
                        <MessageCircle className="h-5 w-5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Format Info Dialog */}
        {isLeague && (
          <FormatInfoDialog
            formatType={event.leagueFormat as any}
            formatName={
              event.leagueFormat === 'round-robin' ? 'Round Robin' : 
              event.leagueFormat === 'sumatoria-juegos' ? 'Sumatoria de Juegos' : 
              event.leagueFormat === 'escalera-piramidal' ? 'Escalera Piramidal' : 
              'Formato de Liga'
            }
            sport={event.sport}
            modality={event.matchType}
            groupRules={event.group_rules}
            additionalRules={event.additional_rules}
            gameDays={event.game_days}
            startTime={event.start_time}
            endTime={event.end_time}
            isOpen={showFormatDialog}
            onClose={() => setShowFormatDialog(false)}
          />
        )}

      {/* Image Fullscreen Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none [&>button]:text-white">
          <img 
            src={event.imageUrl || event.image_url || "/lovable-uploads/bb567a48-6ee6-4eea-917e-c2e42d492ea0.png"} 
            alt={event.title}
            className="w-full h-full object-contain max-h-[90vh]"
          />
        </DialogContent>
      </Dialog>
      </div>
    );
  };

export default EventRegistrationForm;
